/**
 * Camera API Client
 *
 * Extends the WebRTC API Client base class with typed methods
 * for the SC-410 Camera Remote Management API.
 *
 * Provides fully typed methods for all API endpoints defined in the
 * OpenAPI specification (remote-management-api.json).
 *
 * @example
 * const client = new CameraApiClient(dataChannel)
 * const health = await client.getHealth()
 * console.log(health.camera_id, health.version)
 *
 * const status = await client.getStatus()
 * console.log(status.battery_level, status.disk_used_mb)
 */

import { WebrtcApiClientBase } from "./webrtc_api_client_base"
import type { components } from "./remote-management-api"

/**
 * Type aliases for API schemas
 */
export type CameraSettings = components["schemas"]["Settings"]
export type SystemStatus = components["schemas"]["SystemStatusResponse"]
export type HealthResponse = components["schemas"]["HealthResponse"]
export type CameraEventData = components["schemas"]["CameraEventData"]
export type CameraEventType = components["schemas"]["CameraEventType"]
export type ImageChunkResponse = components["schemas"]["ImageChunkResponse"]

/**
 * Generic API error with status code and optional error message
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public responseBody?: string
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * Camera API Client with typed methods for all Remote Management API endpoints
 */
export class CameraApiClient extends WebrtcApiClientBase {
  /**
   * Get health check information (API version, camera ID)
   * GET /
   */
  async getHealth(): Promise<HealthResponse> {
    const response = await this.request({
      method: "GET",
      path: "/",
      headers: {},
      body: null,
    })

    if (response.status !== 200) {
      throw new ApiError(
        response.status,
        `Health check failed with status ${response.status}`,
        response.body || undefined
      )
    }

    return JSON.parse(response.body || "{}")
  }

  /**
   * Get current system status (battery, network, disk usage)
   * GET /status
   */
  async getStatus(): Promise<SystemStatus> {
    const response = await this.request({
      method: "GET",
      path: "/status",
      headers: {},
      body: null,
    })

    if (response.status !== 200) {
      throw new ApiError(
        response.status,
        `Get status failed with status ${response.status}`,
        response.body || undefined
      )
    }

    return JSON.parse(response.body || "{}")
  }

  /**
   * Get current camera settings/configuration
   * GET /settings
   */
  async getSettings(): Promise<CameraSettings> {
    const response = await this.request({
      method: "GET",
      path: "/settings",
      headers: {},
      body: null,
    })

    if (response.status !== 200) {
      throw new ApiError(
        response.status,
        `Get settings failed with status ${response.status}`,
        response.body || undefined
      )
    }

    return JSON.parse(response.body || "{}")
  }

  /**
   * Update camera settings/configuration
   * POST /settings
   */
  async updateSettings(settings: CameraSettings): Promise<void> {
    const response = await this.request({
      method: "POST",
      path: "/settings",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    })

    if (response.status !== 200) {
      // Try to parse JSONAPI error response from camera
      let errorMessage = `Update settings failed with status ${response.status}`

      if (response.body) {
        try {
          const errorResponse = JSON.parse(response.body)

          // Check for JSONAPI error format
          if (errorResponse.errors && Array.isArray(errorResponse.errors) && errorResponse.errors.length > 0) {
            const firstError = errorResponse.errors[0]
            errorMessage = firstError.detail || firstError.title || errorMessage
          }
        } catch (e) {
          // If parsing fails, use the raw response body as the error message
          errorMessage = response.body
        }
      }

      throw new ApiError(
        response.status,
        errorMessage,
        response.body || undefined
      )
    }
  }

  /**
   * Get the most recently captured image for walk test purposes
   * GET /image
   *
   * Images are transferred in chunks over multiple WebRTC responses.
   * The camera splits the binary JPEG data into chunks, base64-encodes each
   * chunk individually, and sends them in separate responses.
   *
   * This method collects all base64-encoded chunks and concatenates them to
   * form the complete base64-encoded JPEG image.
   *
   * @returns Base64 encoded JPEG image data
   */
  async getLatestImage(): Promise<string> {
    const responseStream = this.streamingRequest({
      method: "GET",
      path: "/image",
      headers: {},
      body: null,
    })

    const chunks: { index: number; data: string }[] = []
    let totalChunks: number | null = null
    let lastStatus: number | null = null

    for await (const response of responseStream) {
      lastStatus = response.status

      if (response.status === 404) {
        throw new ApiError(404, "No image available")
      }

      if (response.status !== 200) {
        throw new ApiError(
          response.status,
          `Get image failed with status ${response.status}`,
          response.body || undefined
        )
      }

      // Check if this is a chunked response
      if (response.chunk_index !== undefined && response.total_chunks !== undefined) {
        chunks.push({
          index: response.chunk_index,
          data: response.body || "",
        })

        totalChunks = response.total_chunks

        // If we have all chunks, we can break early
        if (chunks.length === totalChunks) {
          break
        }
      } else {
        // Non-chunked response (single response with full image)
        return response.body || ""
      }
    }

    // Verify we received all chunks
    if (totalChunks === null || chunks.length !== totalChunks) {
      throw new ApiError(
        lastStatus || 500,
        `Incomplete image transfer: received ${chunks.length} of ${totalChunks || "unknown"} chunks`
      )
    }

    // Sort chunks by index and concatenate
    chunks.sort((a, b) => a.index - b.index)
    const base64Image = chunks.map((chunk) => chunk.data).join("")

    return base64Image
  }

  /**
   * Subscribe to real-time camera events via streaming responses
   * GET /events
   *
   * This method initiates a streaming request where multiple event messages
   * will be sent over the data channel with the same request ID.
   * Each event is yielded as it arrives from the camera.
   *
   * Keepalive messages (event_type: "keepalive") are automatically filtered out
   * and not yielded to the caller.
   *
   * @yields CameraEventData for each event received from the camera (excluding keepalives)
   *
   * @example
   * const client = new CameraApiClient(dataChannel)
   * for await (const event of client.subscribeToEvents()) {
   *   console.log('Event received:', event.event_type, event.timestamp)
   * }
   */
  async *subscribeToEvents(): AsyncGenerator<CameraEventData> {
    const responseStream = this.streamingRequest({
      method: "GET",
      path: "/events",
      headers: {},
      body: null,
    })

    for await (const response of responseStream) {
      if (response.status !== 200) {
        throw new ApiError(
          response.status,
          `Event stream error with status ${response.status}`,
          response.body || undefined
        )
      }

      // Parse the event data from the response body
      if (response.body) {
        try {
          const eventData = JSON.parse(response.body) as CameraEventData

          // Filter out keepalive messages - these are for connection maintenance only
          if (eventData.event_type === "keepalive") {
            console.log("[Camera API Client] Received keepalive event")
            continue
          }

          yield eventData
        } catch (error) {
          console.error("[Camera API Client] Failed to parse event data:", error)
          // Continue to next event instead of breaking the stream
        }
      }
    }
  }

  /**
   * Trigger the camera to capture a new image
   * POST /trigger-capture
   *
   * This is a fire-and-forget operation that returns immediately after sending
   * the trigger command. After calling this, poll the getLatestImage() method
   * to retrieve the newly captured image.
   */
  async triggerImageCapture(): Promise<void> {
    const response = await this.request({
      method: "POST",
      path: "/trigger-capture",
      headers: {},
      body: null,
    })

    if (response.status !== 202) {
      throw new ApiError(
        response.status,
        `Trigger image capture failed with status ${response.status}`,
        response.body || undefined
      )
    }
  }

  /**
   * Restore camera configuration to factory default settings
   * POST /restore-defaults
   *
   * This endpoint triggers the SystemManagement service to restore the default configuration.
   * After restoration, use getSettings() to retrieve the new default configuration.
   */
  async restoreDefaultConfiguration(): Promise<void> {
    const response = await this.request({
      method: "POST",
      path: "/restore-defaults",
      headers: {},
      body: null,
    })

    if (response.status !== 200) {
      throw new ApiError(
        response.status,
        `Restore default configuration failed with status ${response.status}`,
        response.body || undefined
      )
    }
  }
}
